import { mixpanelObject } from 'src/utils/analytics';
import { DynamicObject } from 'src/utils/commonTypes';
import { getActivePartnerSubscriptions } from 'src/utils/contentPartnerHelper';

import { SuggestionType } from './SearchTypes';
import {
  MIXPANEL_SEARCH_SUCCESS,
  MIXPANEL_SEARCH_TYPE,
  MIXPANEL_SEARCH_QUERY,
  MIXPANEL_RESULTS_RETURNED,
  MIXPANEL_RECENT_SEARCH_QUERY_DISPLAYED,
  MIXPANEL_TEXT_MANUAL,
  MIXPANEL_POP_UP_NAME,
  MIXPANEL_CLEAR_ALL_SEARCH_WARNING,
  MIXPANEL_POP_UP_TYPE,
  MIXPANEL_NATIVE,
  MIXPANEL_POP_UP_GROUP,
  MIXPANEL_SEARCH,
  MIXPANEL_ELEMENT,
  MIXPANEL_CROSS,
  MIXPANEL_NO,
  MIXPANEL_BUTTON_TYPE,
  MIXPANEL_CTA,
  MIXPANEL_YES,
  MIXPANEL_VOICE,
  MIXPANEL_VOICE_ERROR_MESSAGE,
  MIXPANEL_AGGREGATOR_USER_TYPE,
  MIXPANEL_SUBSCRIBED,
  MIXPANEL_NOT_SUBSCRIBED,
  MIXPANEL_ACTIVE_PARTNER_SUBSCRIPTION,
  MIXPANEL_EVENTS_CLEAR_SEARCH_HISTORY,
  MIXPANEL_EVENTS_SETTING_CHANGED
} from '../../utils/analytics/mixpanel/mixpanelConstants';
import {
  searchStartedEvent,
  searchCancelledEvent,
  clearSearchHistoryEvent,
  searchClearedEvent,
  popupCTAEvent,
  searchMicrophoneCTAEvent,
  voiceSearchErrorEvent,
  searchButtonClickEvent
} from '../../utils/analytics/mixpanel/mixpanelEventsHelper';
import { userOnboarPopupLaunchEvent } from '../userOnboard/MixpanelHelper';

/* Search started mixpanel event */
export const searchStartedMixpanelEvent = (propertyObj: DynamicObject) => {
  const {
    searchSuccess,
    searchType,
    searchQuery,
    resultsReturned,
    recentSearchData
  } = propertyObj;
  mixpanelObject.setProperty(MIXPANEL_SEARCH_SUCCESS, searchSuccess || 'N/A');
  mixpanelObject.setProperty(MIXPANEL_SEARCH_TYPE, searchType || 'N/A');
  mixpanelObject.setProperty(MIXPANEL_SEARCH_QUERY, searchQuery || 'N/A');
  mixpanelObject.setProperty(
    MIXPANEL_RESULTS_RETURNED,
    resultsReturned || 'N/A'
  );
  mixpanelObject.setProperty(
    MIXPANEL_RECENT_SEARCH_QUERY_DISPLAYED,
    (recentSearchData && recentSearchData.length > 0) || false
  );
  searchStartedEvent();
};

/* Mixpanel event for search cancelled */
export const searchCancelledMixpanelEvent = (
  suggestions: SuggestionType[],
  searchQuery = 'N/A'
) => {
  mixpanelObject.setProperty(MIXPANEL_SEARCH_QUERY, searchQuery);
  mixpanelObject.setProperty(
    MIXPANEL_RESULTS_RETURNED,
    (suggestions && suggestions.length) || 0
  );
  searchCancelledEvent();
};

/* MixPanel clearSearchHistoryEvent */
export const clearSearchHistoryMixpanelEvent = () => {
  mixpanelObject.setProperty(
    MIXPANEL_EVENTS_SETTING_CHANGED,
    MIXPANEL_EVENTS_CLEAR_SEARCH_HISTORY
  );
  clearSearchHistoryEvent();
};

/* Mixpanel event for search cancelled */
export const searchClearedMixpanelEvent = (searchQuery: string) => {
  mixpanelObject.setProperty(MIXPANEL_SEARCH_TYPE, MIXPANEL_TEXT_MANUAL);
  mixpanelObject.setProperty(MIXPANEL_SEARCH_QUERY, searchQuery || 'N/A');
  searchClearedEvent();
};

/* Mixpanel event for Confirm popup */
export const confirmRemovePopupMixpanelEvent = () => {
  mixpanelObject.setProperty(
    MIXPANEL_POP_UP_NAME,
    MIXPANEL_CLEAR_ALL_SEARCH_WARNING
  );
  mixpanelObject.setProperty(MIXPANEL_POP_UP_TYPE, MIXPANEL_NATIVE);
  mixpanelObject.setProperty(MIXPANEL_POP_UP_GROUP, MIXPANEL_SEARCH);
  userOnboarPopupLaunchEvent();
};

export const handleCloseMixpanelEvent = (closeButton: boolean) => {
  mixpanelObject.setProperty(
    MIXPANEL_POP_UP_NAME,
    MIXPANEL_CLEAR_ALL_SEARCH_WARNING
  );
  mixpanelObject.setProperty(MIXPANEL_POP_UP_TYPE, MIXPANEL_NATIVE);
  mixpanelObject.setProperty(MIXPANEL_POP_UP_GROUP, MIXPANEL_SEARCH);
  mixpanelObject.setProperty(
    MIXPANEL_ELEMENT,
    closeButton ? MIXPANEL_CROSS : MIXPANEL_NO
  );
  mixpanelObject.setProperty(MIXPANEL_BUTTON_TYPE, MIXPANEL_CTA);
  popupCTAEvent();
};

export const removeAllRecentSearchMixpanelEvent = () => {
  mixpanelObject.setProperty(
    MIXPANEL_POP_UP_NAME,
    MIXPANEL_CLEAR_ALL_SEARCH_WARNING
  );
  mixpanelObject.setProperty(MIXPANEL_POP_UP_TYPE, MIXPANEL_NATIVE);
  mixpanelObject.setProperty(MIXPANEL_POP_UP_GROUP, MIXPANEL_SEARCH);
  mixpanelObject.setProperty(MIXPANEL_ELEMENT, MIXPANEL_YES);
  mixpanelObject.setProperty(MIXPANEL_BUTTON_TYPE, MIXPANEL_CTA);
  popupCTAEvent();
  removeTextMixpanelEvent();
};

export const updateSearchStringMixpanelEvent = (
  suggestionResult: SuggestionType[],
  searchTextTrimValue: string
) => {
  const propertyObj = {
    searchSuccess: (suggestionResult && suggestionResult.length) || false,
    searchType: MIXPANEL_TEXT_MANUAL,
    searchQuery: searchTextTrimValue.length ? searchTextTrimValue : 'N/A',
    resultsReturned: (suggestionResult && suggestionResult.length) || 0
  };
  searchStartedMixpanelEvent(propertyObj);
};

/* Mixpanel event for mic button */
export const getMicMixpanelEvent = () => {
  const propertyObj = {
    searchType: MIXPANEL_VOICE
  };
  searchStartedMixpanelEvent(propertyObj);
  mixpanelObject.setProperty(MIXPANEL_ELEMENT, MIXPANEL_VOICE);
  searchMicrophoneCTAEvent();
};

/* Mixpanel event for voice error message */
export const voiceErrorMixpanelEvent = (errorMessage: string) => {
  mixpanelObject.setProperty(MIXPANEL_VOICE_ERROR_MESSAGE, errorMessage);
  voiceSearchErrorEvent();
};

/* Mixpanel event when search focus */
export const searchFocusMixpanelEvent = () => {
  const activePartnerSubscriptions = getActivePartnerSubscriptions();
  mixpanelObject.setProperty(MIXPANEL_ELEMENT, MIXPANEL_SEARCH);
  mixpanelObject[MIXPANEL_AGGREGATOR_USER_TYPE] = activePartnerSubscriptions
    ? MIXPANEL_SUBSCRIBED
    : MIXPANEL_NOT_SUBSCRIBED;
  mixpanelObject[MIXPANEL_ACTIVE_PARTNER_SUBSCRIPTION] =
    activePartnerSubscriptions || 'N/A';
  searchButtonClickEvent();
};

/* Clear recent search */
export const removeTextMixpanelEvent = () => {
  /* MixPanel clearSearchHistoryEvent */
  mixpanelObject.setProperty(
    MIXPANEL_EVENTS_SETTING_CHANGED,
    MIXPANEL_EVENTS_CLEAR_SEARCH_HISTORY
  );
  clearSearchHistoryEvent();
};
